import {
  ArrowLeftOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  Card,
  Empty,
  Flex,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  updateDoc,
  doc,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { db_fire } from "../firebase";
import moment from "moment";
import Loading from "../components/Loading";

const { Text } = Typography;

const statusColor = {
  active: "green",
  locked: "gray",
  used: "blue",
  expired: "red",
};

const statusIcon = {
  active: <GiftOutlined />,
  locked: <LockOutlined />,
  used: <CheckCircleOutlined />,
  expired: <ClockCircleOutlined />,
};

export default function Coupon() {
  const { userDetails } = useSelector((s) => s.user);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userDetails?._id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db_fire, "coupons"),
      where("userId", "==", userDetails._id)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          _id: d.id,
          ...d.data(),
        }));
        setCoupons(list);
        setLoading(false);
      },
      (err) => {
        console.error("Coupon list error 👉", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [userDetails?._id]);

  const handlePlatformChange = async (couponId, platform) => {
    try {
      await updateDoc(doc(db_fire, "coupons", couponId), {
        platform,
      });
      message.success("Platform updated");
    } catch (err) {
      console.error(err);
      message.error("Failed to update platform");
    }
  };

  return (
    <>
      {loading && <Loading />}

      {/* HEADER */}
      <Flex align="center" gap={10}>
        <ArrowLeftOutlined onClick={() => navigate(-1)} />
        <Text strong>My Coupons</Text>
      </Flex>

      <br />

      {/* EMPTY */}
      {coupons.length < 1 ? (
        <Empty description="No coupons found" />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }}>
          {coupons.map((c) => {
            const isExpired =
              c.expiresAt?.toMillis() < Date.now() &&
              c.status === "active";

            return (
              <Card key={c._id} size="small">
                <Flex justify="space-between" align="center">
                  <Space direction="vertical" size={0}>
                    <Text strong copyable>{c.couponCode}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Expires:{" "}
                      {moment(c.expiresAt?.toDate()).format("DD MMM YYYY")}
                    </Text>
                  </Space>

                  <Tag
                    color={
                      isExpired ? "red" : statusColor[c.status]
                    }
                    icon={statusIcon[c.status]}
                  >
                    {isExpired ? "EXPIRED" : c.status.toUpperCase()}
                  </Tag>
                </Flex>

                <br />

                {/* PLATFORM */}
                {c.status === "active" && !isExpired ? (
                  <>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Select platform
                    </Text>
                    <Select
                      value={c.platform}
                      style={{ width: "100%" }}
                      onChange={(v) =>
                        handlePlatformChange(c._id, v)
                      }
                      options={[
                        { label: "Zomato", value: "zomato" },
                        { label: "Swiggy", value: "swiggy" },
                        { label: "Kamuska", value: "kamuska" },
                        { label: "Ola", value: "ola" },
                      ]}
                    />
                  </>
                ) : (
                  <Flex align="center" gap={6}>
                    <EditOutlined />
                    <Text>{c.platform}</Text>
                  </Flex>
                )}
              </Card>
            );
          })}
        </Space>
      )}
    </>
  );
}
